import API from "@irati/utils/services/API"
import client, {WatchQueryOptions} from "@irati/utils/services/apollo"
import graph from "@irati/utils/services/graph"
import {LoginActiveSession, User, UnauthedBusiness, Business} from "@irati/types"
import PBService from "@irati/utils/services"
import authentication from "@irati/auth"
import {isGlobalAdmin} from "@irati/identity/helpers/user/role"
import FeatureFlags from "@perkbox/feature-flags"
import loginErrorCodes from "../errorCodes/Login"
import AuthStream from "../streams/auth"
import mfa from "../utils/mfa"
import loginErrorHandler from "./errors/legacy/loginErrorHandler"
import {toString as loginQuery} from "./queries/login"
import {toString as ssoLoginQuery} from "./queries/ssoLogin"
import activeSessionsQuery from "./queries/loginActiveSessions"
import loginRemoveActiveSessionMutation from "./mutations/loginRemoveActiveSession"
import loginLogoutMutation from "./mutations/loginLogout"
import tokens from "./tokens"
import mfaTotpService from "./mfa_totp"
import mfaService from "./mfa"
import authErrorHandler from "./errors/authErrorHandler"

interface ActiveSessions {
  loginActiveSessions: LoginActiveSession[]
}

class Login extends PBService {
  watchActive(userId = authentication.user().uuid, options: WatchQueryOptions = {}) {
    const variables = {userId}
    return client.watchQuery<ActiveSessions>({...options, query: activeSessionsQuery, variables})
  }

  removeActiveSession(sessionId: string, userId = authentication.user().uuid): Promise<boolean> {
    const variables = {userId, sessionId}

    return client.mutate({mutation: loginRemoveActiveSessionMutation, variables}).then(({data}) => {
      const cachedData = client.readQuery<ActiveSessions>({query: activeSessionsQuery, variables: {userId}})
      if (cachedData?.loginActiveSessions) {
        client.writeQuery({
          query: activeSessionsQuery,
          variables: {userId},
          data: {
            loginActiveSessions: cachedData.loginActiveSessions.filter((t) => t.uuid !== sessionId)
          }
        })
      }
      return data.loginRemoveActiveSession
    })
  }

  logout({redirectOnLogout = true, logOutMutation = true} = {}): Promise<boolean> {
    if (API.hasTokens() && logOutMutation) {
      client.mutate({mutation: loginLogoutMutation})
    }
    const original = AuthStream.shouldRedirectOnLogout
    AuthStream.shouldRedirectOnLogout = redirectOnLogout
    return AuthStream.stop().then(() => {
      AuthStream.shouldRedirectOnLogout = original
      return true
    })
  }

  async login(tenant: UnauthedBusiness, email: string, password: string): Promise<void> {
    const options = {
      useErrorHandler: false,
      unAuth: true
    }
    API.sentryIgnoreError(options, [loginErrorCodes.LoginChallengeNewPasswordRequired, loginErrorCodes.UserNotFound])
    const variables = {
      email,
      password,
      tenantId: tenant.uuid
    }
    const data = await graph({query: loginQuery, variables, operationName: "Login"}, options)
    const {accessToken, ...rest} = data.login
    // Clear any stale session cookie on this tenant subdomain before setting the new credentials.
    // Without this, repeat logins hit a server-side cookie/token conflict on /set-credentials
    // which causes the resume query in AuthStream to fail and log the user back out.
    await tokens.clear()
    await tokens.set({accessToken})
    await forceMFASetup(rest.business, rest.user)
    return AuthStream.start(rest)
  }

  async ssoLogin(accessToken: string): Promise<void> {
    const options = {
      useErrorHandler: false
    }
    // See login() above for the rationale on clearing before setting.
    await tokens.clear()
    await tokens.set({accessToken})
    const data = await graph({query: ssoLoginQuery, operationName: "SSOLogin"}, options)
    await forceMFASetup(data.login.business, data.login.user, {skipMFAForAllUsers: true})
    return AuthStream.start(data.login)
  }

  async oneTimePasswordLogin(accessToken: string): Promise<void> {
    // See login() above for the rationale on clearing before setting.
    await tokens.clear()
    await tokens.set({accessToken})
    const data = await graph({query: ssoLoginQuery, operationName: "SSOLogin"})
    await forceMFASetup(data.login.business, data.login.user)
    return AuthStream.start(data.login)
  }
}

const loginService = new Login({errorHandlers: [loginErrorHandler, authErrorHandler]})

export default loginService

async function forceMFASetup(business: Business, user: User, options?: {skipMFAForAllUsers?: boolean}) {
  // killswitch variable to easily revert MFA to just admins for all tenants
  const SHOW_MFA_FOR_ALL_USERS = true
  const hasMFAForAllUsers = business.featureFlags?.includes(FeatureFlags.AuthMFAAllUsers)
  const skipMFAForAllUsers = options?.skipMFAForAllUsers ?? false
  const isAdmin = isGlobalAdmin(business, user)

  if (SHOW_MFA_FOR_ALL_USERS && hasMFAForAllUsers && !skipMFAForAllUsers) {
    try {
      await new Promise((resolve, reject) => {
        mfa.start(resolve, reject, isAdmin)
      })
    } catch (e) {
      const err = new Error() as PBError
      err.message = "platform.mfa.mfaError.usersRequired"
      throw err
    }
  } else if (isAdmin) {
    const mfaSetup = await mfaService.getUser(user.uuid!)
    const mfaTotpSetup = await mfaTotpService.getUser(user.uuid!)
    const hasSetupSMS = mfaSetup && mfaSetup.isConfirmed
    const hasSetupTotp = mfaTotpSetup && mfaTotpSetup.isConfirmed

    if (!hasSetupSMS && !hasSetupTotp) {
      try {
        await mfaService.hasFlag()
      } catch (e) {
        const err = new Error() as PBError
        err.message = "platform.mfa.mfaError.usersRequired"
        throw err
      }
    }
  }
}
